import type { IVariantConfig } from "@/variants/types";
import React, { createContext, useContext } from "react";

/**
 * Client-side access to the active variant config.
 *
 * The server resolves the config via `getVariantConfig()` (index.server.ts) and passes it into
 * `VariantProvider` (typically from the root loader). Components read it with `useVariant()`.
 */
export const VariantContext = createContext<IVariantConfig | null>(null);

export function VariantProvider({
    config,
    children,
}: {
    config: IVariantConfig;
    children: React.ReactNode;
}) {
    return (
        <VariantContext.Provider value={config}>{children}</VariantContext.Provider>
    );
}

/** Returns the active variant config. Throws if used outside `VariantProvider`. */
export function useVariant(): IVariantConfig {
    const config = useContext(VariantContext);
    if (!config) {
        throw new Error("useVariant() must be used within a <VariantProvider>.");
    }
    return config;
}
